/**
 * Repaints the catalog's detail-header card so it follows the topic drill-down.
 *
 * Pure renderer — it binds nothing. `CatalogNavigationClass` owns the one delegated click listener on
 * the page and calls in here. The website catalog has no such card and cheat sheets opts out by design,
 * so every method has to survive a missing root.
 */
export default class CatalogHeader {
    constructor() {
        this.classList = {
            root: 'js-catalog-header',
            cover: 'js-catalog-header-cover',
            band: 'js-catalog-header-band',
            coverHiddenClass: 'hidden',
            title: 'js-catalog-header-title',
            desc: 'js-catalog-header-desc',
            meta: 'js-catalog-header-meta',
            count: 'js-catalog-header-count',
            customize: 'js-open-customize-panel',
            settings: 'js-open-topic-settings',
        };

        // The cover swap is async, so a fast second click must be able to disown the first one's load.
        this.pendingCover = '';

        const {
            root,
            cover,
            band,
            title,
            desc,
            meta,
            count,
            customize,
            settings,
        } = this.classList;

        this.card = document.querySelector(`.${root}`);
        if (!this.card) {
            return;
        }

        this.coverEl = this.card.querySelector(`.${cover}`);
        this.bandEl = this.card.querySelector(`.${band}`);
        this.titleEl = this.card.querySelector(`.${title}`);
        this.descEl = this.card.querySelector(`.${desc}`);
        this.metaEl = this.card.querySelector(`.${meta}`);
        this.countEl = this.card.querySelector(`.${count}`);
        this.customizeEl = this.card.querySelector(`.${customize}`);
        this.settingsEl = this.card.querySelector(`.${settings}`);

        // Both states are read off the served markup, so PHP owns every string and a restore can never
        // word it differently from a reload. Topic values cache; root values are getters — see below.
        const data = this.card.dataset;
        this.topicPosition = data.topicPosition || '';
        this.labelCourse = data.labelCourse || '';
        this.labelCourses = data.labelCourses || '';
    }

    /**
     * ⛔ Read at paint time, never cached: the Customize panel edits the account identity in place and
     * writes it back onto these attributes, so a cached copy would undo the edit on the way back from
     * a topic.
     *
     * @returns {string}
     */
    get rootTitle() {
        return this.card.dataset.rootTitle || '';
    }

    /** @returns {string} See `rootTitle`. */
    get rootDesc() {
        return this.card.dataset.rootDesc || '';
    }

    /** @returns {string} See `rootTitle`. */
    get rootCover() {
        return this.card.dataset.rootCover || '';
    }

    /** @returns {string} See `rootTitle`. */
    get rootPosition() {
        return this.card.dataset.rootPosition || '';
    }

    /** @returns {string} CSS, not a URL, so it is restored apart from `rootCover`. See `rootTitle`. */
    get rootBackground() {
        return this.card.dataset.rootBackground || '';
    }

    /**
     * Paints the clicked topic's identity over the account's.
     *
     * @param {HTMLElement} element Any `.open-topic__js` — a sidebar link, a grid tile, or the sidebar
     *      twin a breadcrumb crumb resolved to.
     */
    renderForTopic(element) {
        if (!this.card || !element) {
            return;
        }

        const cover = this.resolveCover(element.dataset.cover);

        this.paint({
            isTopic: true,
            title: element.dataset.name || '',
            desc: '',
            // A topic with no art of its own keeps the account cover rather than blanking the banner.
            cover: cover || this.rootCover,
            position: cover ? this.topicPosition : this.rootPosition,
            // A topic's own art is always a picture; without one it keeps the account cover, background and all.
            background: cover ? '' : this.rootBackground,
            count: this.countLabel(element.dataset.count),
            topicId: element.dataset.topic || '',
        });
    }

    /** Puts the account identity back. */
    renderRoot() {
        if (!this.card) {
            return;
        }

        this.paint({
            isTopic: false,
            title: this.rootTitle,
            desc: this.rootDesc,
            cover: this.rootCover,
            position: this.rootPosition,
            background: this.rootBackground,
            count: '',
            topicId: '',
        });
    }

    paint(state) {
        if (this.titleEl) {
            this.titleEl.textContent = state.title;
        }
        if (this.descEl) {
            this.descEl.textContent = state.desc;
            // Topics have no description of their own, so the line is the account identity's.
            this.descEl.hidden = state.isTopic;
        }
        this.setCover(state.cover, state.position, state.background);
        if (this.countEl) {
            this.countEl.textContent = state.count;
        }
        if (this.metaEl) {
            this.metaEl.hidden = !state.isTopic || state.count === '';
        }
        if (this.customizeEl) {
            this.customizeEl.hidden = state.isTopic;
        }
        if (this.settingsEl) {
            this.settingsEl.hidden = !state.isTopic;
        }
        this.card.dataset.topicId = state.topicId;
    }

    /**
     * Swaps the banner art without letting the crop move ahead of it.
     *
     * ⛔ Setting `src` and `object-position` in the same tick repaints the OLD image at the NEW crop
     * until the new file arrives — a visible jump, and worst leaving the root, whose account cover
     * carries a brand-picker position far from centre (67.71% on staging). Decode off-DOM, then
     * change both together.
     *
     * A background has nothing to decode and paints in the same tick; coming BACK to a picture, the
     * band is cleared where the `src` changes, for the same reason the crop is.
     */
    setCover(src, position, background = '') {
        if (!this.coverEl) {
            return;
        }
        if (background) {
            // Disowns any load still in flight: its `onload` would un-hide the image over the band.
            this.pendingCover = '';
            this.paintBand(background);
            return;
        }
        if (!src) {
            return;
        }
        if (src === this.coverEl.src) {
            this.paintBand('');
            this.coverEl.style.objectPosition = position;
            return;
        }

        this.pendingCover = src;
        // ⛔ Also disowns the load when ANYONE else writes the element meanwhile - the cover picker
        // paints a pick straight onto this banner, and a drill-down still decoding would otherwise
        // land afterwards and restore the art the pick replaced.
        const paintedFrom = this.coverEl.getAttribute('src');
        const next = new Image();

        next.onload = () => {
            if (this.pendingCover !== src || this.coverEl.getAttribute('src') !== paintedFrom) {
                return;
            }
            this.paintBand('');
            this.coverEl.src = src;
            this.coverEl.style.objectPosition = position;
        };

        // Same rule the server applies when a cover file is missing: fall back to the account art.
        next.onerror = () => {
            if (this.pendingCover !== src || this.coverEl.getAttribute('src') !== paintedFrom) {
                return;
            }
            this.paintBand(this.rootBackground);
            this.coverEl.src = this.rootCover;
            this.coverEl.style.objectPosition = this.rootPosition;
        };

        next.src = src;
    }

    /**
     * Paints the band and parks the image, or clears both — `components/common/cover-band` owns the
     * `hidden` half.
     *
     * @param {string} background CSS colour or gradient; '' restores the picture.
     */
    paintBand(background) {
        if (this.bandEl) {
            this.bandEl.style.background = background;
        }
        if (this.coverEl) {
            this.coverEl.classList.toggle(this.classList.coverHiddenClass, !!background);
        }
    }

    /** @returns {string} An absolute URL, or '' when the level carries no cover of its own. */
    resolveCover(raw) {
        if (!raw) {
            return '';
        }
        if (/^(?:https?:)?\/\//.test(raw)) {
            return raw;
        }

        // Server-rendered triggers already carry a webroot-rooted path (the helper resolved it against
        // the filesystem); client-rendered tiles still hand over the raw column value.
        return window.location.origin + (raw.charAt(0) === '/' ? raw : webrootPath + raw);
    }

    /** @returns {string} `N courses`, or '' when the level carries no count. */
    countLabel(raw) {
        const count = parseInt(raw, 10);
        if (!Number.isFinite(count)) {
            return '';
        }

        return `${count} ${count === 1 ? this.labelCourse : this.labelCourses}`;
    }
}
